/**
 * HTML del bloque de retroalimentación en modales SweetAlert (quiz).
 * Colores por zona: Scenario2 naranja, 3 violeta, 4 azul, 5 verde.
 */

const THEME_MODIFIER_CLASS = {
	orange: "quiz-feedback-block--orange",
	violet: "quiz-feedback-block--violet",
	blue: "quiz-feedback-block--blue",
	green: "quiz-feedback-block--green",
};

function escapeHtml(str) {
	return String(str ?? "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#039;");
}

/** Tras escapar todo, reintroduce solo etiquetas inline muy acotadas (p. ej. <i> en textos fijos del repo). */
function restoreAllowedInlineTags(escaped) {
	return escaped
		.replace(/&lt;i&gt;/gi, "<i>")
		.replace(/&lt;\/i&gt;/gi, "</i>")
		.replace(/&lt;em&gt;/gi, "<em>")
		.replace(/&lt;\/em&gt;/gi, "</em>")
		.replace(/&lt;b&gt;/gi, "<b>")
		.replace(/&lt;\/b&gt;/gi, "</b>")
		.replace(/&lt;strong&gt;/gi, "<strong>")
		.replace(/&lt;\/strong&gt;/gi, "</strong>");
}

/**
 * @param {string} retroalimentacion - texto; admite <i>, <em>, <b>, <strong> (sin atributos) para cursiva/negrita
 * @param {"orange"|"violet"|"blue"|"green"} theme
 * @returns {string} fragmento HTML seguro para interpolar en textHtml
 */
export function buildQuizFeedbackBlockHtml(retroalimentacion, theme = "orange") {
	const modifier = THEME_MODIFIER_CLASS[theme] || THEME_MODIFIER_CLASS.orange;
	const bodyHtml = restoreAllowedInlineTags(escapeHtml(retroalimentacion)).replace(/\n/g, "<br/>");
	return `
    <div class="quiz-feedback-block ${modifier} txt20">
      <div class="quiz-feedback-block__inner">
        <div class="quiz-feedback-block__accent" aria-hidden="true"></div>
        <div class="quiz-feedback-block__body">
          <div class="quiz-feedback-block__text">${bodyHtml}</div>
        </div>
      </div>
    </div>`;
}

const CORRECT_SHELL_BG = {
	orange: "linear-gradient(135deg, rgba(45, 212, 191, 0.55), rgba(56, 189, 248, 0.4))",
	violet: "linear-gradient(135deg, rgba(167, 139, 250, 0.62), rgba(124, 58, 237, 0.42))",
	blue: "linear-gradient(135deg, rgba(96, 165, 250, 0.62), rgba(59, 130, 246, 0.42))",
	green: "linear-gradient(135deg, rgba(52, 211, 153, 0.62), rgba(16, 185, 129, 0.42))",
};

const INCORRECT_SHELL_BG =
	"linear-gradient(135deg, rgba(248, 113, 113, 0.58), rgba(239, 68, 68, 0.48))";

const WEIGHTED_SHELL_BG =
	"linear-gradient(135deg, rgba(251, 191, 36, 0.62), rgba(245, 158, 11, 0.45))";

/**
 * Avatar de resultado (sin texto incrustado) + etiqueta «Correcta» / «Incorrecta» / «Valoración»,
 * marco redondeado y sombra alineados con sg5-video-shell.
 * @param {string} imageUrl URL del asset (import de webpack).
 * @param {"correct"|"incorrect"|"weighted"} outcome
 * @param {"orange"|"violet"|"blue"|"green"} theme color del escenario (borde / pill acierto).
 */
export function buildQuizResultAvatarHtml(imageUrl, outcome, theme = "orange") {
	const safeTheme = CORRECT_SHELL_BG[theme] ? theme : "orange";
	let shellStyle;
	let label;
	let badgeClass;

	if (outcome === "weighted") {
		shellStyle = `style="background:${WEIGHTED_SHELL_BG}"`;
		label = "Valoración";
		badgeClass = "quiz-result-badge quiz-result-badge--weighted";
	} else if (outcome === "correct") {
		shellStyle = `style="background:${CORRECT_SHELL_BG[safeTheme]}"`;
		label = "Correcta";
		badgeClass = `quiz-result-badge quiz-result-badge--correct quiz-result-badge--t-${safeTheme}`;
	} else {
		shellStyle = `style="background:${INCORRECT_SHELL_BG}"`;
		label = "Incorrecta";
		badgeClass = "quiz-result-badge quiz-result-badge--incorrect";
	}

	return `<div class="quiz-result-avatar-row quiz-result-avatar-row--tight" data-qfb-theme="${safeTheme}">
  <span class="${badgeClass}">${label}</span>
  <div class="quiz-result-avatar-shell" ${shellStyle}>
    <div class="quiz-result-avatar-inner">
      <img src="${imageUrl}" alt="" class="quiz-result-avatar-img" />
    </div>
  </div>
</div>`;
}

/**
 * Avatar de especialista (mismo marco/sombra que resultado de quiz), centrado, sin etiqueta.
 * @param {string} imageUrl URL del asset (import de webpack).
 * @param {"orange"|"violet"|"blue"|"green"} theme color del escenario (degradado del marco).
 */
export function buildSpecialistHelpAvatarHtml(imageUrl, theme = "orange") {
	const safeTheme = CORRECT_SHELL_BG[theme] ? theme : "orange";
	const shellStyle = `style="background:${CORRECT_SHELL_BG[safeTheme]}"`;
	return `<div class="specialist-help-avatar" data-qfb-theme="${safeTheme}">
  <div class="quiz-result-avatar-shell specialist-help-avatar__shell" ${shellStyle}>
    <div class="quiz-result-avatar-inner">
      <img src="${imageUrl}" alt="" class="quiz-result-avatar-img" />
    </div>
  </div>
</div>`;
}

/**
 * Degradado del marco (mismo que acierto en quiz) para pantallas «Excelente» / fin de escenario.
 * @param {"orange"|"violet"|"blue"|"green"} theme
 */
export function getFinExcelenteShellBackground(theme = "orange") {
	const safe = CORRECT_SHELL_BG[theme] ? theme : "orange";
	return CORRECT_SHELL_BG[safe];
}
